/**
 * CreateWorkflowDialog — Multi-step workflow creation with AI Supervisor config
 *
 * Step 1: Identity (name, description, template)
 * Step 2: Purpose & Intelligence (purpose statement, success criteria, constraints, supervisor mode)
 * Step 3: Trigger (schedule, event, webhook, manual + type-specific config)
 *
 * On complete → navigates to workflow builder with trigger node pre-placed.
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Workflow, Plus, X, ChevronRight, ChevronLeft,
  Zap, Clock, Globe, Play, Brain, Shield,
  Sparkles, AlertTriangle, CheckCircle, Trash2, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  CRON_PRESETS,
  EVENT_TYPE_LABELS,
  type SupervisorMode,
  type CriteriaType,
  type CriteriaEval,
  type CriteriaWeight,
  type SuccessCriterion,
  type WorkflowTriggerType,
  type EventType,
} from '@/lib/mock-automate-data';
import { useCreateWorkflow } from '@/lib/use-automate';
import { uuidv4 } from '@/lib/uuid';

interface CreateWorkflowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STEPS = [
  { id: 1, label: 'Identity', icon: FileText },
  { id: 2, label: 'Purpose & Intelligence', icon: Brain },
  { id: 3, label: 'Trigger', icon: Zap },
];

export function CreateWorkflowDialog({ open, onOpenChange }: CreateWorkflowDialogProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1: Identity
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [template, setTemplate] = useState('blank');

  // Step 2: Purpose & Intelligence
  const [purpose, setPurpose] = useState('');
  const [criteria, setCriteria] = useState<SuccessCriterion[]>([]);
  const [constraints, setConstraints] = useState<string[]>([]);
  const [supervisorMode, setSupervisorMode] = useState<SupervisorMode>('standard');
  // New criterion form
  const [newCriterionDesc, setNewCriterionDesc] = useState('');
  const [newCriterionType, setNewCriterionType] = useState<CriteriaType>('completeness');
  const [newCriterionEval, setNewCriterionEval] = useState<CriteriaEval>('ai');
  const [newCriterionWeight, setNewCriterionWeight] = useState<CriteriaWeight>('critical');
  // New constraint
  const [newConstraint, setNewConstraint] = useState('');

  // Step 3: Trigger
  const [triggerType, setTriggerType] = useState<WorkflowTriggerType>('manual');
  const [cronExpression, setCronExpression] = useState('0 6 * * *');
  const [eventType, setEventType] = useState<EventType>('alarm_triggered');
  const [webhookUrl, setWebhookUrl] = useState('');

  const canProceed = () => {
    if (step === 1) return name.trim().length > 0;
    if (step === 2) return purpose.trim().length > 0;
    if (step === 3) return true;
    return false;
  };

  const handleAddCriterion = () => {
    if (!newCriterionDesc.trim()) return;
    setCriteria(prev => [...prev, {
      id: `sc-new-${Date.now()}`,
      description: newCriterionDesc.trim(),
      type: newCriterionType,
      evaluation: newCriterionEval,
      weight: newCriterionWeight,
    }]);
    setNewCriterionDesc('');
  };

  const handleRemoveCriterion = (id: string) => {
    setCriteria(prev => prev.filter(c => c.id !== id));
  };

  const handleAddConstraint = () => {
    if (!newConstraint.trim()) return;
    setConstraints(prev => [...prev, newConstraint.trim()]);
    setNewConstraint('');
  };

  const handleRemoveConstraint = (index: number) => {
    setConstraints(prev => prev.filter((_, i) => i !== index));
  };

  const createWf = useCreateWorkflow();

  const handleComplete = () => {
    if (createWf.isPending) return;
    const triggerKindForApi: 'scheduled' | 'event' | 'webhook' | 'manual' =
      triggerType === 'schedule' ? 'scheduled' : triggerType;
    // Seed the canvas with a Trigger node so the builder is immediately
    // usable — matches the n8n-style "every workflow starts with a
    // trigger" pattern (#102, #123).
    const triggerLabel =
      triggerType === 'schedule' ? 'Scheduled Trigger'
      : triggerType === 'event' ? 'Event Trigger'
      : triggerType === 'webhook' ? 'Webhook Trigger'
      : 'Manual Trigger';
    const triggerConfig: Record<string, unknown> = { triggerType };
    if (triggerType === 'schedule') triggerConfig.cronExpression = cronExpression;
    if (triggerType === 'event') triggerConfig.eventType = eventType;

    // #237 — Simple Agent template promised "Trigger → Agent (2 nodes)"
    // but the wizard only ever shipped a single Trigger node. Now
    // honor the user's choice: when template === 'simple', also
    // add an Agent node and a connecting edge so the builder opens
    // with the advertised graph.
    const triggerNodeId = uuidv4();
    const agentNodeId = uuidv4();
    const isSimpleAgent = template === 'simple';

    const wizardNodes = isSimpleAgent
      ? [
          {
            id: triggerNodeId,
            nodeKind: 'trigger',
            label: triggerLabel,
            positionX: 100,
            positionY: 100,
            config: triggerConfig,
          },
          {
            id: agentNodeId,
            nodeKind: 'agent',
            label: 'Agent',
            positionX: 100,
            positionY: 280,
            config: {},
          },
        ]
      : [
          {
            id: triggerNodeId,
            nodeKind: 'trigger',
            label: triggerLabel,
            positionX: 100,
            positionY: 100,
            config: triggerConfig,
          },
        ];

    const wizardEdges = isSimpleAgent
      ? [
          {
            sourceNodeId: triggerNodeId,
            targetNodeId: agentNodeId,
            animated: true,
          },
        ]
      : [];

    createWf.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        purpose: purpose.trim() || undefined,
        triggerType: triggerKindForApi,
        cronExpression: triggerType === 'schedule' ? cronExpression : null,
        eventType: triggerType === 'event' ? eventType : null,
        supervisorMode,
        complexity: 'simple',
        nodes: wizardNodes,
        edges: wizardEdges,
        successCriteria: criteria.map((c) => ({
          description: c.description,
          criteriaType: c.type,
          evaluation: c.evaluation,
          weight: c.weight,
        })),
        constraints,
      },
      {
        onSuccess: ({ id }) => {
          toast.success(`Workflow "${name}" created`, {
            description: 'Opening builder…',
          });
          onOpenChange(false);
          resetForm();
          navigate({ to: '/automate/workflows/$id', params: { id } } as never);
        },
        onError: (err) => {
          toast.error('Could not create workflow', {
            description: err.message ?? 'Server returned an error.',
          });
        },
      },
    );
  };

  const resetForm = () => {
    setStep(1);
    setName(''); setDescription(''); setTemplate('blank');
    setPurpose(''); setCriteria([]); setConstraints([]); setSupervisorMode('standard');
    setNewCriterionDesc(''); setNewConstraint('');
    setTriggerType('manual'); setCronExpression('0 6 * * *');
    setEventType('alarm_triggered'); setWebhookUrl('');
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Workflow className="h-5 w-5 text-primary" />
            Create Workflow
          </DialogTitle>
          {/* #763 — Radix Dialog requires an accessible description or
              explicit aria-describedby={undefined}. Provide a real
              description so screen-reader users hear what the wizard
              does and the console no longer logs the missing-
              DialogDescription warning. */}
          <DialogDescription>
            Multi-step wizard to create a new workflow with trigger,
            steps, and AI supervisor configuration.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 py-2">
          {STEPS.map((s, i) => {
            const StepIcon = s.icon;
            const isActive = step === s.id;
            const isComplete = step > s.id;
            return (
              <div key={s.id} className="flex items-center gap-2">
                {i > 0 && <div className={cn('h-px w-8', isComplete ? 'bg-primary' : 'bg-border')} />}
                <div className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                  isActive ? 'bg-primary text-primary-foreground' : isComplete ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                )}>
                  {isComplete ? <CheckCircle className="h-3 w-3" /> : <StepIcon className="h-3 w-3" />}
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>

        <Separator />

        {/* Step content */}
        <div className="flex-1 overflow-auto py-4 space-y-4">

          {/* ════════════════ STEP 1: Identity ════════════════ */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Workflow Name<span className="text-red-700 dark:text-red-300 ml-0.5">*</span></Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Equipment Troubleshooting Pipeline" className="h-9" maxLength={80} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description of what this workflow does" rows={2} className="text-sm" maxLength={300} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Start From</Label>
                <RadioGroup value={template} onValueChange={setTemplate} className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'blank', label: 'Blank', desc: 'Start from scratch' },
                    { value: 'simple', label: 'Simple Agent', desc: 'Trigger → Agent (2 nodes)' },
                    { value: 'playbook', label: 'From Playbook', desc: 'Use a template' },
                  ].map(opt => (
                    <Label key={opt.value} className={cn('flex flex-col gap-1 p-3 rounded-lg border cursor-pointer transition-colors', template === opt.value ? 'border-primary bg-primary/5' : 'hover:border-primary/30')}>
                      <div className="flex items-center gap-1.5">
                        <RadioGroupItem value={opt.value} />
                        <span className="text-xs font-medium">{opt.label}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground">{opt.desc}</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          {/* ════════════════ STEP 2: Purpose & Intelligence ════════════════ */}
          {step === 2 && (
            <div className="space-y-5">
              {/* Purpose */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1">
                  <Brain className="h-3 w-3 text-primary" />
                  Purpose<span className="text-red-700 dark:text-red-300 ml-0.5">*</span>
                </Label>
                <Textarea value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="What should this workflow achieve? Describe the goal in natural language. The AI supervisor will evaluate outputs against this purpose." rows={3} className="text-sm" />
                <p className="text-[10px] text-muted-foreground">The AI supervisor uses this to understand what quality looks like for this workflow.</p>
              </div>

              {/* Success Criteria */}
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1">
                  <CheckCircle className="h-3 w-3 text-emerald-500" />
                  Success Criteria
                </Label>
                <p className="text-[10px] text-muted-foreground">Define measurable quality checks the supervisor evaluates at each step and the final output.</p>

                {criteria.length > 0 && (
                  <div className="space-y-1.5">
                    {criteria.map(c => (
                      <div key={c.id} className="flex items-start gap-2 bg-muted/30 rounded-lg px-3 py-2 group">
                        <CheckCircle className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs">{c.description}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Badge variant="outline" className="text-[8px]">{c.type}</Badge>
                            <Badge variant="outline" className="text-[8px]">{c.evaluation}</Badge>
                            <Badge variant="outline" className={cn('text-[8px]', c.weight === 'critical' ? 'text-red-700 dark:text-red-300' : c.weight === 'important' ? 'text-amber-800 dark:text-amber-300' : 'text-zinc-700 dark:text-zinc-300')}>{c.weight}</Badge>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-destructive shrink-0" onClick={() => handleRemoveCriterion(c.id)} aria-label="Remove criterion" title="Remove">
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add criterion form */}
                <div className="border rounded-lg p-3 space-y-2">
                  <Input value={newCriterionDesc} onChange={e => setNewCriterionDesc(e.target.value)} placeholder="e.g., Report must include all alarms from the shift period" className="h-8 text-xs" onKeyDown={e => { if (e.key === 'Enter') handleAddCriterion(); }} />
                  <div className="flex items-center gap-2">
                    <Select value={newCriterionType} onValueChange={v => setNewCriterionType(v as CriteriaType)}>
                      <SelectTrigger className="h-7 text-[10px] w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="completeness">Completeness</SelectItem>
                        <SelectItem value="accuracy">Accuracy</SelectItem>
                        <SelectItem value="format">Format</SelectItem>
                        <SelectItem value="safety">Safety</SelectItem>
                        <SelectItem value="compliance">Compliance</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={newCriterionEval} onValueChange={v => setNewCriterionEval(v as CriteriaEval)}>
                      <SelectTrigger className="h-7 text-[10px] w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ai">AI Evaluated</SelectItem>
                        <SelectItem value="deterministic">Rule-Based</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={newCriterionWeight} onValueChange={v => setNewCriterionWeight(v as CriteriaWeight)}>
                      <SelectTrigger className="h-7 text-[10px] w-24"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="important">Important</SelectItem>
                        <SelectItem value="nice_to_have">Nice to Have</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-7 text-xs gap-1 ml-auto" onClick={handleAddCriterion} disabled={!newCriterionDesc.trim()}>
                      <Plus className="h-3 w-3" /> Add
                    </Button>
                  </div>
                </div>
              </div>

              {/* Constraints */}
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                  Constraints
                </Label>
                <p className="text-[10px] text-muted-foreground">What should this workflow NOT do? The supervisor enforces these rules.</p>

                {constraints.length > 0 && (
                  <div className="space-y-1">
                    {constraints.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 bg-amber-500/5 rounded-lg px-3 py-1.5 group">
                        <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
                        <span className="text-xs flex-1">{c}</span>
                        <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-destructive shrink-0" onClick={() => handleRemoveConstraint(i)} aria-label="Remove constraint" title="Remove">
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Input value={newConstraint} onChange={e => setNewConstraint(e.target.value)} placeholder="e.g., Never acknowledge alarms autonomously" className="h-8 text-xs flex-1" onKeyDown={e => { if (e.key === 'Enter') handleAddConstraint(); }} />
                  <Button size="sm" className="h-8 text-xs gap-1" onClick={handleAddConstraint} disabled={!newConstraint.trim()}>
                    <Plus className="h-3 w-3" /> Add
                  </Button>
                </div>
              </div>

              {/* Supervisor Mode */}
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1">
                  <Shield className="h-3 w-3 text-primary" />
                  AI Supervisor Mode
                </Label>
                <RadioGroup value={supervisorMode} onValueChange={v => setSupervisorMode(v as SupervisorMode)} className="space-y-2">
                  {[
                    { value: 'minimal', label: 'Minimal', desc: 'Validates only the final output. Fastest, lowest cost. Good for simple workflows.', color: 'text-zinc-700 dark:text-zinc-300' },
                    { value: 'standard', label: 'Standard', desc: 'Validates each agent node output. Can request corrections. Default for most workflows.', color: 'text-blue-700 dark:text-blue-300' },
                    { value: 'thorough', label: 'Thorough', desc: 'Validates every step, cross-checks data, runs faithfulness verification. For safety-critical workflows.', color: 'text-violet-700 dark:text-violet-300' },
                  ].map(opt => (
                    <Label key={opt.value} className={cn('flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors', supervisorMode === opt.value ? 'border-primary bg-primary/5' : 'hover:border-primary/30')}>
                      <RadioGroupItem value={opt.value} className="mt-0.5" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Brain className={cn('h-3 w-3', opt.color)} />
                          <span className="text-xs font-medium">{opt.label}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{opt.desc}</p>
                      </div>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          {/* ════════════════ STEP 3: Trigger ════════════════ */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs">How should this workflow start?</Label>
                <RadioGroup value={triggerType} onValueChange={v => setTriggerType(v as WorkflowTriggerType)} className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'schedule', icon: Clock, label: 'Schedule', desc: 'Run on a cron schedule (daily, weekly, etc.)' },
                    { value: 'event', icon: Zap, label: 'Event', desc: 'Triggered by an event (alarm, threshold, etc.)' },
                    { value: 'webhook', icon: Globe, label: 'Webhook', desc: 'Triggered by an HTTP request from external system' },
                    { value: 'manual', icon: Play, label: 'Manual', desc: 'Run manually by a user from the dashboard' },
                  ].map(opt => {
                    const TIcon = opt.icon;
                    return (
                      <Label key={opt.value} className={cn('flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors', triggerType === opt.value ? 'border-primary bg-primary/5' : 'hover:border-primary/30')}>
                        <RadioGroupItem value={opt.value} className="mt-0.5" />
                        <div>
                          <div className="flex items-center gap-1.5">
                            <TIcon className="h-3 w-3" />
                            <span className="text-xs font-medium">{opt.label}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{opt.desc}</p>
                        </div>
                      </Label>
                    );
                  })}
                </RadioGroup>
              </div>

              {/* Schedule config */}
              {triggerType === 'schedule' && (
                <div className="space-y-2">
                  <Label className="text-xs">Cron Schedule</Label>
                  <Select value={cronExpression} onValueChange={setCronExpression}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CRON_PRESETS.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Or enter custom expression:</Label>
                    <Input value={cronExpression} onChange={e => setCronExpression(e.target.value)} className="h-8 text-xs font-mono" placeholder="0 6 * * *" />
                  </div>
                </div>
              )}

              {/* Event config */}
              {triggerType === 'event' && (
                <div className="space-y-2">
                  <Label className="text-xs">Event Type</Label>
                  <Select value={eventType} onValueChange={v => setEventType(v as EventType)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Webhook config */}
              {triggerType === 'webhook' && (
                <div className="space-y-2">
                  <Label className="text-xs">Webhook Endpoint</Label>
                  <Input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} placeholder="Will be generated after creation" className="h-9 text-xs font-mono" disabled />
                  <p className="text-[10px] text-muted-foreground">A unique webhook URL will be generated when the workflow is created.</p>
                </div>
              )}

              {/* Manual — no config needed */}
              {triggerType === 'manual' && (
                <div className="bg-muted/30 rounded-lg p-4 text-center">
                  <Play className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">This workflow will be triggered manually from the dashboard using the "Run" button.</p>
                </div>
              )}

              {/* Summary */}
              <div className="border rounded-lg p-3 space-y-2 bg-muted/20">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Summary</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{name}</span></div>
                  <div><span className="text-muted-foreground">Trigger:</span> <span className="font-medium capitalize">{triggerType}</span></div>
                  <div><span className="text-muted-foreground">Supervisor:</span> <span className="font-medium capitalize">{supervisorMode}</span></div>
                  <div><span className="text-muted-foreground">Criteria:</span> <span className="font-medium">{criteria.length}</span></div>
                  <div className="col-span-2"><span className="text-muted-foreground">Purpose:</span> <span className="font-medium italic">{purpose ? `"${purpose.substring(0, 80)}${purpose.length > 80 ? '...' : ''}"` : 'Not set'}</span></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer navigation */}
        <Separator />
        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" size="sm" onClick={() => step > 1 ? setStep(step - 1) : onOpenChange(false)} className="gap-1">
            <ChevronLeft className="h-3.5 w-3.5" />
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground">Step {step} of 3</span>
            {step < 3 ? (
              <Button size="sm" onClick={() => setStep(step + 1)} disabled={!canProceed()} className="gap-1">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleComplete}
                disabled={createWf.isPending}
                className="gap-1"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {createWf.isPending ? 'Creating…' : 'Create & Open Builder'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
